<template>
  <div class="c-press-hold">
    <div class="title">
      {{ $t("keyboard.any.title") }}
    </div>
    <div class="fields">
      <!-- Press 按键选择 -->
      <c-universal-virtual-keyboard
        :selectedKey="convertToKeyboardFormat(anyKey.first_key)"
        format="keyboard"
        :autoHeight="true"
        :extraKeys="extraPressKeys"
        :disabled="!selectedKey"
        @change="onPressKeyChange"
        :class="{ 'disabled-keyboard': !selectedKey }"
      >
        <md-field 
          class="field" 
          :class="{ 
            'md-has-value': anyKey.first_key,
            'md-disabled': !selectedKey
          }"
        >
          <label for="press">{{ $t("keyboard.any.press") }}</label>
          <md-input
            :value="pressKeyText"
            readonly
            :disabled="!selectedKey"
            class="key-input"
          ></md-input>
        </md-field>
      </c-universal-virtual-keyboard>
      
      <!-- Hold 按键选择 -->
      <c-universal-virtual-keyboard
        :selectedKey="convertToKeyboardFormat(anyKey.second_key)"
        format="keyboard"
        :autoHeight="true"
        :extraKeys="extraHoldKeys"
        :disabled="!selectedKey"
        @change="onHoldKeyChange"
        :class="{ 'disabled-keyboard': !selectedKey }"
      >
        <md-field 
          class="field" 
          :class="{ 
            'md-has-value': anyKey.second_key,
            'md-disabled': !selectedKey
          }"
        >
          <label for="hold">{{ $t("keyboard.any.hold") }}</label>
          <md-input
            :value="holdKeyText"
            readonly
            :disabled="!selectedKey"
            class="key-input"
          ></md-input>
        </md-field>
      </c-universal-virtual-keyboard>
      
      <md-field class="field">
        <label>{{ $t("keyboard.any.interval") }}</label>
        <md-input
          v-model="anyKey.interval_time"
          @blur="onIntervalInput"
          type="number"
          min="10"
          max="15000"
          :disabled="disabled || !selectedKey"
        ></md-input>
        <span class="md-suffix">ms</span>
      </md-field>
    </div>
    <md-button
      class="button"
      :disabled="
        disabled ||
        !selectedKey ||
        !anyKey.first_key ||
        !anyKey.second_key ||
        !anyKey.interval_time
      "
      @click="onAnyOk"
    >
      {{ $t("yes") }}
    </md-button>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { anyKeys } from "./SpecialKeys";

export default {
  name: "CPressHold",
  props: {
    dispatchType: String,
    profile: {
      type: Object,
      default: () => {},
    },
    selectedKey: String,
    selectedKeyIndex: Number,
    layerIndex: Number,
    pressKeys: {
      type: Object,
      default: () => {},
    },
    holdKeys: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      anyKeys,
      anyKey: {
        ph_key_data: "",
        first_key: null,
        second_key: null,
        interval_time: 200,
      },
      disabled: false,
    };
  },
  computed: {
    ...mapState("app", {
      isCN: "isCN",
    }),
    // Press 按键显示文本
    pressKeyText() {
      return this.getKeyText(this.anyKey.first_key);
    },
    // Hold 按键显示文本
    holdKeyText() {
      return this.getKeyText(this.anyKey.second_key);
    },
    // Press 额外按键（从 pressKeys 中提取非标准按键）
    extraPressKeys() {
      if (!this.pressKeys) return null;
      
      const extra = {};
      for (const key in this.pressKeys) {
        // 提取非标准 HID 按键（媒体键、功能键等）
        // 标准按键格式：#00070xxx
        // 媒体键格式：#000C00xx
        // 功能键格式：#009200xx 或 #0092xxxx
        if (!key.startsWith('#000700')) {
          extra[key] = this.pressKeys[key];
        }
      }
      
      return Object.keys(extra).length > 0 ? extra : null;
    },
    // Hold 额外按键（从 holdKeys 中提取非标准按键）
    extraHoldKeys() {
      if (!this.holdKeys) return null;
      
      const extra = {};
      for (const key in this.holdKeys) {
        // 提取非标准 HID 按键（媒体键、功能键等）
        // 标准按键格式：#00070xxx
        // 媒体键格式：#000C00xx
        // 功能键格式：#009200xx 或 #0092xxxx
        if (!key.startsWith('#000700')) {
          extra[key] = this.holdKeys[key];
        }
      }
      
      return Object.keys(extra).length > 0 ? extra : null;
    },
  },
  methods: {
    /**
     * 获取按键显示文本
     * @param {string} key - 按键码（#00070004 格式）
     * @returns {string} 显示文本
     */
    getKeyText(key) {
      if (!key) return '';
      
      // 直接从 pressKeys/holdKeys 查找（格式已统一为 #00070004）
      const pressKey = this.pressKeys && this.pressKeys[key];
      const holdKey = this.holdKeys && this.holdKeys[key];
      
      if (pressKey) {
        let text = pressKey.nowrapText || pressKey.text || '';
        // 去掉 HTML 标签（如 <br/>）
        return text.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '');
      }
      
      if (holdKey) {
        let text = holdKey.nowrapText || holdKey.text || '';
        // 去掉 HTML 标签（如 <br/>）
        return text.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '');
      }
      
      // 如果都没找到，显示按键码
      return key;
    },
    /**
     * 将 #00 格式转换为虚拟键盘需要的 #11 格式
     * @param {string} key - 按键码（#00 格式 #00070004）
     * @returns {string} keyboard 格式（#11 格式 #11070004）
     */
    convertToKeyboardFormat(key) {
      if (!key) return '';
      if (key.startsWith('#00')) {
        return key.replace(/^#00/, '#11');
      }
      return key;
    },
    /**
     * Press 按键变更回调
     * @param {string} value - 新的按键码（#00070004 格式）
     */
    onPressKeyChange(value) {
      this.anyKey.first_key = value;
    },
    /**
     * Hold 按键变更回调
     * @param {string} value - 新的按键码（#00070004 格式）
     */
    onHoldKeyChange(value) {
      this.anyKey.second_key = value;
    },
    onAnyClick() {
      this.disabled = false;
      let data = this.profile.data;
      if (
        data.tab_key.length >= 199 &&
        !this.anyKeys.includes(this.selectedKey)
      ) {
        this.disabled = true;
        this.$message.info(this.$t("keyboard.any.max"));
      }
      let newKey = "";
      for (let i = 0; i < 200; i++) {
        const key = "#009514" + i.toString(16).toUpperCase().padStart(2, "0");
        const anyKey = data.tab_key.find((k) => k.ph_key_data === key);
        if (!anyKey || !anyKey.first_key) {
          newKey = key;
          break;
        }
      }
      if (this.anyKeys.includes(this.selectedKey)) {
        const anyKey = data.tab_key.find(
          (k) => k.ph_key_data === this.selectedKey
        );
        this.anyKey = {
          ...anyKey,
          ph_key_data: newKey,
          layer_num: this.layerIndex, //当前键层
          row_num: Math.floor(this.selectedKeyIndex / 25), //行
          index: this.selectedKeyIndex % 25, //列
          old_key: this.selectedKey, //新键值
          new_key: newKey, //新键值
        };
      } else {
        this.anyKey = {
          first_key: null,
          second_key: null,
          interval_time: 200,
          ph_key_data: newKey,
          layer_num: this.layerIndex, //当前键层
          row_num: Math.floor(this.selectedKeyIndex / 25), //行
          index: this.selectedKeyIndex % 25, //列
          old_key: this.selectedKey, //新键值
          new_key: newKey, //新键值
        };
      }
    },
    onIntervalInput(e) {
      let time = e.target.value;
      if (time < 10) time = 10;
      if (time > 15000) time = 15000;
      this.anyKey.interval_time = Math.floor(time);
      this.anyKey = { ...this.anyKey };
    },
    onAnyOk() {
      let data = this.profile.data;
      if (this.anyKeys.includes(this.selectedKey)) {
        const index = data.tab_key.findIndex(
          (k) => k.ph_key_data === this.selectedKey
        );
        if (index > -1) {
          data.tab_key[index] = this.anyKey;
          this.$store.dispatch(this.dispatchType, data);
        }
      } else {
        data.tab_key.push(this.anyKey);
        this.$store.dispatch(this.dispatchType, data);
      }
      this.$emit("key-click", this.anyKey.ph_key_data);
    },
  },
  watch: {
    selectedKey(selectedKey) {
      if (selectedKey) this.onAnyClick();
    },
  },
  mounted() {
    if (this.selectedKey) this.onAnyClick();
  },
};
</script>
<style lang="scss" scoped>
.c-press-hold {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;

  .title {
    font-size: 18px;
    color: #ffffff;
    margin-bottom: 30px;
  }

  .fields {
    @include flex-center;
    margin-bottom: 30px;

    .field {
      width: 180px;

      + .field {
        margin-left: 20px;
      }

      .md-input {
        width: 140px;
      }

      .key-input {
        cursor: pointer;
        pointer-events: none;
        font-size: 14px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  // 禁用虚拟键盘时不可点击
  .disabled-keyboard {
    pointer-events: none;
    cursor: not-allowed;
  }

  .button {
    @include button;
    border: 1px solid $primary-color;
    border-radius: 5px;
    background: transparent;
    color: $primary-color;

    + .button {
      margin-left: 70px;
      background: $primary-color;
      color: #ffffff;
    }

    &:hover {
      color: #ffffff;
    }
  }
}
</style>
